import { Link } from "react-router-dom";
import { Container, Flex, Button, Skeleton, Box } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { useDashboardNavigation } from "../../hooks/useDashboardNavigation";
import { LanguageSwitcher } from "../LanguageSwitcher";
import styles from "./PublicPageLayout.module.css";

interface PublicPageLayoutProps {
  children: React.ReactNode;
  /** Use gradient background (default: true) */
  gradientBackground?: boolean;
  /** Additional class name for the page wrapper */
  className?: string;
}

function PublicToolbar() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { loading: roleLoading, navigateToDashboard } =
    useDashboardNavigation();

  const isLoading = authLoading || (user && roleLoading);

  return (
    <header className={styles.toolbar}>
      <Container size="4">
        <Flex justify="end" align="center" gap="3" p="3">
          <LanguageSwitcher />
          {isLoading ? (
            <Skeleton>
              <Button>Loading...</Button>
            </Skeleton>
          ) : user ? (
            <Button onClick={navigateToDashboard}>
              {t("onePager.goToDashboard")}
            </Button>
          ) : (
            <Button asChild variant="surface">
              <Link to="/login">{t("onePager.alreadyHaveAccount")}</Link>
            </Button>
          )}
        </Flex>
      </Container>
    </header>
  );
}

export function PublicPageLayout({
  children,
  gradientBackground = true,
  className,
}: PublicPageLayoutProps) {
  return (
    <div
      className={`${styles.page} ${className || ""}`}
      style={
        gradientBackground
          ? {
              background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            }
          : undefined
      }
    >
      <PublicToolbar />
      <Box pt="6">{children}</Box>
    </div>
  );
}

// Export the toolbar separately for cases where more control is needed
export { PublicToolbar };
